import _ from "lodash";

/**
 * @constant SANITIZE_COLUMN_ALIGMENT_REGEX
 * @description Finds all spaces and quotes (single/double)
 */
const SANITIZE_COLUMN_ALIGMENT_REGEX = new RegExp(/[\s'"]+/, 'g');
const ALIGNMENTS = ["left", "center", "right"];
const VALID_STRING_WIDTHS = ["auto", "equal"];
const TOTAL_COLUMN_WIDTH = 10;

const TableTypes = {
    STANDARD: "standard",
    GLOSSARY: "glossary",
};

const PropTypes = ["type", "collapse", "widths", "headerAlign", "textAlign", "columnAlign", "columnAlignHeader", "mdAlign"].reduce((acc, propName) => {
    acc[_.snakeCase(propName).toUpperCase()] = {    // eg.  COLUMN_ALIGN: {
        prop: propName,                             //          prop: "columnAlign",
        key: _.kebabCase(propName),                 //          key: "column-align",
    };                                              //      }
    return acc;
}, {});

const RowTypes = {
    HEADER: {
        type: "header",
        alignClass: "header",
        element: "th",
    },
    DATA: {
        type: "data",
        alignClass: "text",
        element: "td",
    },
};

const ErrorTypes = {
    GLOSSARY_WIDTH_HELP: `In a glossary-type table, the first column to use the \`${PropTypes.WIDTHS.prop}\` prop is the second column. Eg. If there are 3 columns, the \`${PropTypes.WIDTHS.prop}\` prop could be [3, 7]`,
    INVALID_WIDTH: `The \`${PropTypes.WIDTHS.prop}\` prop should be one of "auto" (default), "equal" or an array containing the width of each column in 1/${TOTAL_COLUMN_WIDTH} increments adding up to ${TOTAL_COLUMN_WIDTH}. Eg. [5, 2, 3].`,
    INVALID_WIDTH_SUM: `The \`${PropTypes.WIDTHS.prop}\` prop should be an array containing the width of each column in 1/${TOTAL_COLUMN_WIDTH} increments adding up to ${TOTAL_COLUMN_WIDTH}. Eg. [5, 2, 3].`,
    INVALID_WIDTH_LEN: `The \`${PropTypes.WIDTHS.prop}\` prop should contain as many numbers as there are columns`,
    GLOSSARY_COLUMN_HELP: `In a glossary-type table, the first column to use the \`columnAlign\` prop is the second column. Eg. If there are 3 columns, the \`${PropTypes.COLUMN_ALIGN.prop}\` prop could be ["left", "center"]`,
    INVALID_COLUMN_ALIGN: `The \`${PropTypes.COLUMN_ALIGN.prop}\` prop should be an array with containing the text alignment of each column. Options include "left", "center" or "right". Eg. ["left", "center", "left"]`,
    INVALID_COLUMN_ALIGN_LEN: `The \`${PropTypes.COLUMN_ALIGN.prop}\` prop should contain as many alignments as there are columns`,
    INVALID_TEXT_ALIGN: propName => `The \`${propName}\` prop should be one of "left", "center" or "right"`,
    INVALID_BOOLEAN: propName => `The \`${propName}\` prop must be boolean. Options include "true" or "false"`,
};

class TableError extends Error  {
    constructor(message, tableType, propName, value) {
        let _message = "An error occurred in the table shortcode.";
        if (propName) {
            _message = _message.concat(" ", `An invalid \`${propName}\` prop was specified. ${value} was given.`)
        }

        if (message) {
            _message = _message.concat(" ", message);
            if (tableType === TableTypes.GLOSSARY) {
                if (propName === PropTypes.WIDTHS.prop) {
                    _message = _message.concat(" ", ErrorTypes.GLOSSARY_WIDTH_HELP);
                } else if (propName === PropTypes.COLUMN_ALIGN.prop) {
                    _message = _message.concat(" ", ErrorTypes.GLOSSARY_COLUMN_HELP);
                }
            }

        } else {
            _message = "An unknown error occurred in the table shortcode."
        }

        super(_message);
        this.name = "TableError";
    }
};

const makeColumnAlignments = str => {
    if (_.isString(str)) {
        return _.trim(str.replace(SANITIZE_COLUMN_ALIGMENT_REGEX, ""), "[]").split(",");
    }
    return undefined;
}

export default class Tables {
    validateBoolean(prop, propName, tableType) {
        if (_.isBoolean(prop)) {
            return true;
        }
        throw new TableError(ErrorTypes.INVALID_BOOLEAN(propName), tableType, propName, prop);
    }

    validateAlignment(alignment, propName, tableType) {
        if (_.isString(alignment)) {
            if (ALIGNMENTS.includes(alignment)) {
                return true;
            }
        }
        throw new TableError(ErrorTypes.INVALID_TEXT_ALIGN(propName), tableType, propName, alignment);
    }

    validateColumnAlignment(columnAlign, columnCount, tableType) {
        const propName = PropTypes.COLUMN_ALIGN.prop;

        if (_.isArray(columnAlign)) {
            if (columnAlign.length === columnCount) {
                if (columnAlign.every(alignment => ALIGNMENTS.includes(alignment))) {
                    return true;
                }
                throw new TableError(ErrorTypes.INVALID_COLUMN_ALIGN, tableType, propName, columnAlign);
            }
            throw new TableError(ErrorTypes.INVALID_COLUMN_ALIGN_LEN, tableType, propName, columnAlign);
        }
        throw new TableError(ErrorTypes.INVALID_COLUMN_ALIGN, tableType, propName, columnAlign);
    }

    validateWidths(widths, cols, tableType) {
        const propName = PropTypes.WIDTHS.prop;

        if (_.isString(widths)) {
            if (VALID_STRING_WIDTHS.includes(widths)) {
                return true;
            }
            throw new TableError(ErrorTypes.INVALID_WIDTH, tableType, propName, widths);
        } else if (_.isArray(widths)) {
            if (widths.length === cols) {
                if (_.sum(widths) === TOTAL_COLUMN_WIDTH) {
                    return true;
                }
                throw new TableError(ErrorTypes.INVALID_WIDTH_SUM, tableType, propName, widths);
            }
            throw new TableError(ErrorTypes.INVALID_WIDTH_LEN, tableType, propName, widths);
        }
        throw new TableError(ErrorTypes.INVALID_WIDTH, tableType, propName, widths);
    };

    validateProps($element, columnCount, tableType) {
        const collapse = $element.data(PropTypes.COLLAPSE.key);
        const widths = $element.data(PropTypes.WIDTHS.key);
        const headerAlign = $element.data(PropTypes.HEADER_ALIGN.key);
        const textAlign = $element.data(PropTypes.TEXT_ALIGN.key);
        const columnAlign = makeColumnAlignments($element.data(PropTypes.COLUMN_ALIGN.key))
        const columnAlignHeader = $element.data(PropTypes.COLUMN_ALIGN_HEADER.key);

        const props = {
            collapse,
            widths,
            headerAlign,
            textAlign,
            columnAlign,
            columnAlignHeader,
        };

        if (
            this.validateAlignment(headerAlign, PropTypes.HEADER_ALIGN.prop, tableType) &&
            this.validateAlignment(textAlign, PropTypes.TEXT_ALIGN.prop, tableType) &&
            this.validateWidths(widths, columnCount, tableType) &&
            this.validateBoolean(collapse, PropTypes.COLLAPSE.prop, tableType)
        ) {
            if (
                columnAlign &&
                this.validateColumnAlignment(columnAlign, columnCount, tableType) &&
                this.validateBoolean(columnAlignHeader, PropTypes.COLUMN_ALIGN_HEADER.prop, tableType)
            ) {
                return props;
            }
            return props;
        }

        throw new TableError();
    }

    createGlossaryRow({
        rowType,
        cells,
        columnAlign,
        widths,
        equalWidth,
    }) {
        const $row = $(document.createElement("tr")).addClass(`row-${rowType.type}`);
        
        cells.forEach((cell, idx) => {
            const $cell = $(document.createElement(rowType.element))
                .addClass(`glossary-${rowType.type}`)
                .html(cell);

            if (rowType.type === RowTypes.HEADER.type) {
                $cell.attr("scope", "column");
            }

            if (columnAlign) {
                $cell.addClass(`${rowType.alignClass}-align-${columnAlign[idx]}`)
            }

            if (equalWidth) {
                $cell.width(equalWidth);
            } else if (_.isArray(widths)) {
                $cell.addClass(`width-${widths[idx]}`)
            }

            $row.append($cell);
        });

        return $row;
    }

    mount({ tableSelector, standardTableModifier, glossaryTableModifier }) {
        // Using document.createElement() for performance. See https://stackoverflow.com/a/268520/12628480
        
        const tableShortcodeClass = "table-modifier";
        const tableShortcodeSelector = `.${tableShortcodeClass}`;

        const tableWrapperClass = "table-wrapper";
        const tableWrapperSelector = `.${tableWrapperClass}`;

        $(tableSelector).each((idx, table) => {
            const $table = $(table);

            $table.attr("role", "table");

            // Select all the cells in the first row and check if they have an `align` attribute
            // We do this because markdown has the ability to generate tables with alignment attributes.
            const $firstRowCells = $.makeArray($table.find("tr:first").children());
            if ($firstRowCells.some(cell => $(cell).attr('align'))) {
                
                // Must remain in sync with the defaults defined in shortcodes/table.html
                let $shortcode = $(document.createElement('div'))
                    .addClass("table-standard")
                    .addClass(tableShortcodeClass)
                    .data(PropTypes.TYPE.key, TableTypes.STANDARD)
                    .data(PropTypes.COLLAPSE.key, false)
                    .data(PropTypes.WIDTHS.key, "auto")
                    .data(PropTypes.HEADER_ALIGN.key, "left")
                    .data(PropTypes.TEXT_ALIGN.key, "left");

                const tableHasShortcode = Boolean($table.prev(tableShortcodeSelector).length);
                if (tableHasShortcode) {
                    $shortcode = $table.prev(tableShortcodeSelector);
                }

                // Only create a column align data attribute if it doesn't yet exist
                if (!$shortcode.data(PropTypes.COLUMN_ALIGN.key)) {
                    $shortcode.data(PropTypes.MD_ALIGN.key, true); // Indicates that this table is aligned with markdown syntax

                    const columnAlign = $firstRowCells.map(cell => {
                        const $cell = $(cell);
                        const alignment = $cell.attr("align");

                        $cell.removeAttr("align");
                        return alignment || "left";
                    });

                    $shortcode
                        .data(PropTypes.COLUMN_ALIGN.key, `[${columnAlign.join(',')}]`)
                        .data(PropTypes.COLUMN_ALIGN_HEADER.key, true);

                    if (!tableHasShortcode) {
                        $table.before($shortcode);
                    }
                }
            }

            // Find the closest previous sibling that has an id attribute and get its id
            const labelledBy = $table.prevAll("*[id]:first").attr("id");
            $table.attr("aria-labelledby", labelledBy);

            $table.wrap($(document.createElement("div")).addClass(tableWrapperClass));
        });

        $(standardTableModifier).each((idx, element) => {
            const $element = $(element);
            const $table = $element.next(tableWrapperSelector).children("table");
            const columnCount = $table.find("tr:first").children().length;

            const {
                collapse,
                widths,
                headerAlign,
                textAlign,
                columnAlign,
                columnAlignHeader,
            } = this.validateProps($element, columnCount, TableTypes.STANDARD)

            $table.addClass("standard")

            if (collapse) {
                $table.addClass("collapse")
            }

            if (columnAlign) {
                $table.find("td")
                    .each((idx, column) => $(column).addClass(`text-align-${columnAlign[idx % columnCount]}`));
                    
                if (columnAlignHeader) {
                    $table.find("th")
                        .each((idx, column) => $(column).addClass(`header-align-${columnAlign[idx % columnCount]}`));
                }

            } else {
                $table.addClass(`header-align-${headerAlign}`);
                $table.addClass(`text-align-${textAlign}`);
            }

            if (widths === "auto") {
                return;

            } else if (widths === "equal") {
                const width = `calc(100% / ${columnCount})`;

                $table.find("tr")
                    .children()
                    .each((idx, cell) => $(cell).width(width));

            } else if (_.isArray(widths)) {
                $table.find("th")
                    .each((idx, header) => $(header).addClass(`width-${widths[idx]}`));
                    
                $table.find("td")
                    .each((idx, column) => $(column).addClass(`width-${widths[idx % columnCount]}`));
            }

            // Only remove the modifier element when the table is valid to
            // make it easier to debug when the table is invalid
            $element.remove();
        });

        $(glossaryTableModifier).each((idx, element) => {
            const $element = $(element);
            const $table = $element.next(tableWrapperSelector).children("table");
            const columnCount = $table.find("tr:first").children().length - 1;

            const {
                collapse,
                widths,
                headerAlign,
                textAlign,
                columnAlign,
            } = this.validateProps($element, columnCount, TableTypes.GLOSSARY)

            let equalWidth;
            if (widths === "equal") {
                equalWidth = `calc(100% / ${columnCount})`;
            }

            const headers = $.makeArray(
                $table.find("th")
            ).map(th => $(th).html()).slice(1);
            
            const rows = $.makeArray(
                $table.find("tbody tr")
            ).map(tr => $.makeArray($(tr).find("td")).map(td => $(td).html()))
            
            const $glossary = $(document.createElement("table"))
                .addClass("glossary")
                .addClass(`header-align-${headerAlign}`)
                .attr("role", "table")
                .append(
                    $(document.createElement("tbody"))
                        .append(
                            rows.reduce(($rows, row) => {
                                const [term, ...cells] = row;

                                const plainTextTerm = $(term).text();
                                const firstColRow = $(document.createElement("tr")).addClass("row-term");
                                firstColRow.append(
                                    $(document.createElement("th"))
                                        .addClass("glossary-term")
                                        .attr("colspan", columnCount)
                                        .attr("scope", "row")
                                        .html(plainTextTerm)
                                );
                                
                                const headerRow = this.createGlossaryRow({
                                    rowType: RowTypes.HEADER,
                                    cells: headers,
                                    columnAlign,
                                    widths,
                                    equalWidth,
                                });
                                
                                const dataRow = this.createGlossaryRow({
                                    rowType: RowTypes.DATA,
                                    cells,
                                    columnAlign,
                                    widths,
                                    equalWidth,
                                });
                                
                                $rows.push(firstColRow);
                                $rows.push(headerRow);
                                $rows.push(dataRow);

                                return $rows;
                            }, [])
                        )
                );

            if (collapse) {
                $glossary.addClass("collapse");
            }
            
            if (!columnAlign) {
                $glossary.addClass(`text-align-${textAlign}`);
            }

            $table.replaceWith($glossary);

            // Only remove the modifier element when the table is valid to
            // make it easier to debug when the table is invalid
            $element.remove();
        });
    }
}



// WEBPACK FOOTER //
// ./themes/docs/static/js/tables.js